// # Filename: src/components/modals/MultiplayerResultModal.jsx

import React, { useEffect, useMemo, useState } from "react";
import styles from "./MultiplayerResultModal.module.css";
import { AiFillHome, AiOutlineCheck, AiOutlineClose } from "react-icons/ai";
import { LuRefreshCcw } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { useGameWebSocketContext } from "../../websocket/GameWebsocketContext";
import { useCountdown } from "../../hooks/ui/useCountdown";

export const MultiplayerResultModal = ({ isGameOver, winner, playerRole }) => {
  const navigate = useNavigate();
  const { dispatch, sendMessage, state } = useGameWebSocketContext();
  const [showResultActions, setShowResultActions] = useState(false);

  const {
    isRematchOfferVisible,
    rematchMessage,
    rematchPending,
    game,

    // (server-authoritative fields from reducer)
    rematchShowActions,
    rematchUiMode,
    rematchRequesterUserId,
    rematchReceiverUserId,
    rematchCreatedAtMs,
    rematchGameId,
    rematchButtonLocked,
  } = state;

  // Step 1: Determine whether THIS client should see Accept/Decline
  // Server now controls this via showActions, so we do not infer roles anymore.
  const shouldShowAcceptDecline = Boolean(rematchShowActions);

  // Step 2: Requester view = sees waiting/countdown (never action buttons)
  // If uiMode exists, use it. Otherwise fallback to "not receiver".
  const isRequesterView = useMemo(() => {
    if (!isRematchOfferVisible) return false;
    if (rematchUiMode) return rematchUiMode === "requester";
    return !shouldShowAcceptDecline;
  }, [isRematchOfferVisible, rematchUiMode, shouldShowAcceptDecline]);

  // Step 3: If game becomes active again (new game), ensure modal is closed
  useEffect(() => {
    if (!state.isCompleted) {
      dispatch({ type: "HIDE_REMATCH_MODAL" });
    }
  }, [state.isCompleted, dispatch]);

  useEffect(() => {
    if (!isGameOver) {
      setShowResultActions(false);
      return undefined;
    }

    const id = window.setTimeout(() => setShowResultActions(true), 1100);
    return () => window.clearTimeout(id);
  }, [isGameOver]);

  // Step 4: Debug instrumentation at the UI decision point
  useEffect(() => {
    if (!isRematchOfferVisible) return;

    console.log("[REMATCH][UI_DECISION]", {
      ts: Date.now(),
      gameIdFromState: game?.id,
      rematchGameId,
      rematchCreatedAtMs,
      shouldShowAcceptDecline,
      isRequesterView,
      rematchRequesterUserId,
      rematchReceiverUserId,
      rematchPending,
      rematchMessage,
    });
  }, [
    isRematchOfferVisible,
    game?.id,
    rematchGameId,
    rematchCreatedAtMs,
    shouldShowAcceptDecline,
    isRequesterView,
    rematchRequesterUserId,
    rematchReceiverUserId,
    rematchPending,
    rematchMessage,
  ]);

  // Step 5: Countdown only for requester view
  const shouldRunCountdown = isRematchOfferVisible && isRequesterView;

  const countdown = useCountdown(10, shouldRunCountdown, () => {
    dispatch({ type: "HIDE_REMATCH_MODAL" });
  });

  // Step 6: Actions
  const handleRematchRequest = () => {
    dispatch({ type: "LOCK_REMATCH_BUTTON" });
    sendMessage({ type: "rematch_request" });
  };

  const handleAccept = () => {
    dispatch({ type: "LOCK_REMATCH_BUTTON" });
    dispatch({ type: "HIDE_REMATCH_MODAL" });
    sendMessage({ type: "rematch_accept" });
  };

  const handleDecline = () => {
    // Step 1: Lock immediately to prevent double-send
    dispatch({ type: "LOCK_REMATCH_BUTTON" });

    // Step 2: Close UI immediately (server will also broadcast rematch_declined)
    dispatch({ type: "HIDE_REMATCH_MODAL" });

    // Step 3: Notify server
    sendMessage({ type: "rematch_decline" });
  };

  const handleGoHome = () => {
    dispatch({ type: "RESET_GAME_STATE" });
    navigate("/");
  };

  // Step 7: Winner text
  const resultMessage = (() => {
    if (winner === "D" || winner === null) return "Draw";

    if (playerRole) {
      return winner === playerRole ? "You Won" : "You Lost";
    }

    const winningPlayer =
      winner === "X"
        ? game?.player_x?.first_name || "Player X"
        : winner === "O"
        ? game?.player_o?.first_name || "Player O"
        : null;

    return winningPlayer ? `${winningPlayer} Wins` : `${winner} Wins`;
  })();

  // Step 8: Modal open logic
  const resultModalClasses =
    (isGameOver && showResultActions) || isRematchOfferVisible ? styles.modalOpen : "";
  const resultTone =
    winner === "D" || winner === null
      ? styles.resultDraw
      : playerRole && winner === playerRole
      ? styles.resultWin
      : playerRole
      ? styles.resultLoss
      : "";

  return (
    <div className={`${styles.modalOverlay} ${resultModalClasses}`}>
      <div className={styles.modalBox}>
        <div className={`${styles.winnerText} ${resultTone}`}>{resultMessage}</div>

        <div className={styles.buttonGroup}>
          {isRematchOfferVisible ? (
            shouldShowAcceptDecline ? (
              <>
                <p className={styles.rematchText}>{rematchMessage}</p>

                <div className={styles.rematchButtons}>
                  <button
                    className={styles.iconButton}
                    onClick={handleAccept}
                    disabled={rematchButtonLocked}
                    aria-label="Accept rematch"
                    title="Accept rematch"
                  >
                    <AiOutlineCheck size={15} />
                  </button>

                  <button
                    className={`${styles.iconButton} ${styles.danger}`}
                    onClick={handleDecline}
                    disabled={rematchButtonLocked}
                    aria-label="Decline rematch"
                    title="Decline rematch"
                  >
                    <AiOutlineClose size={15} />
                  </button>
                </div>
              </>
            ) : (
              <p className={styles.rematchText}>
                {`${rematchMessage} Waiting... ${countdown}s`}
              </p>
            )
          ) : (
            <button
              className={styles.iconButton}
              onClick={handleRematchRequest}
              disabled={rematchButtonLocked}
              aria-label="Request rematch"
              title="Request rematch"
            >
              <LuRefreshCcw size={15} />
              <span>Rematch</span>
            </button>
          )}

          <button
            className={styles.iconButton}
            onClick={handleGoHome}
            aria-label="Go home"
            title="Go home"
          >
            <AiFillHome size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};
